import type { OpenClawPluginApi } from "openclaw/plugin-sdk/core";
import type { OpenClawPluginToolContext } from "openclaw/plugin-sdk/core";
import { resolveAccount } from "./accounts"
import { Type } from "@sinclair/typebox";
import { CHANNEL_ID } from './const';
import {sendTextMsg, sendInteractiveMsg} from "./outbound"
import {CHAT_TYPE_DIRECT,CHAT_TYPE_GROUP,CHAT_TYPE_CHANNEL,guessChatType, teamsBuildChatId, parseChatFromSessionKey, parseSessionKey, teamsParseChatId} from "./chat_base"
import {
  buildConfirmCard,
  buildFormCard,
  buildMultiInputCard,
  buildSelectCard,
  buildTuituiInteractiveMessage,
  type CardBody,
} from "./cards"
import { createTuituiClient } from "./sdk"

function requireToolAccount(ctx: OpenClawPluginToolContext) {
  const account = resolveAccount(ctx.config, ctx.agentAccountId, ctx.agentId);
  if (!account || !account.enabled || !account.appId || !account.appSecret) {
    throw new Error(`invalid tuitui account ${ctx.agentAccountId}`);
  }
  return account;
}

const tuitui_bot_get_messages_factory = (ctx: OpenClawPluginToolContext) => {
  return {
    name: "tuitui_bot_get_messages",
    label: "tuitui_bot_get_messages",
    description: "推推(tuitui) 聊天记录获取（机器人身份），可查询私聊、群聊、频道的聊天记录\n\n优先级说明：如果存在 tuitui-cli skill，优先使用tuitui-cli（用户权限更大）。只有用户明确说明“以 bot 身份操作”时，或者不存在 tuitui-cli 才调用本工具",
    parameters: Type.Object({
      chatId: Type.String({ description: "聊天ID，单聊指tuitui用户的account，群聊是群ID，频道指频道ID" }),
      chatType: Type.String({ description: `聊天类型。 单聊:${CHAT_TYPE_DIRECT}  群聊:${CHAT_TYPE_GROUP}  频道:${CHAT_TYPE_CHANNEL}`}),
      relativeTime: Type.Optional(Type.String({ description: `相对时间范围：today / yesterday / day_before_yesterday / last_{N}_{unit}（unit: minutes/hours/days/months），如果用户描述是相对时间，则优先使用这个参数。与 startTime/endTime 互斥`})),
      startTime: Type.Optional(Type.String({ description: `起始时间（ISO 8601 格式，如 2026-02-27T00:00:00+08:00），非必填，默认指2000年。与 relativeTime 互斥`})),
      endTime: Type.Optional(Type.String({ description: `结束时间（ISO 8601 格式，如 2026-02-27T00:00:00+08:00），非必填，默认为now。与 relativeTime 互斥`})),
      limit: Type.Optional(Type.Number({ description: `每页条数，1~100，默认 100`})),
      cursor: Type.Optional(Type.String({ description: `游标首次调用填 "0"，返回结果中has_more为true时代表可以获取下一页，如果你需要下一页，可以传返回结果的cursor代表继续拉取下一页, 带有cursor时，必须要有relativeTime或者startTime参数`})),
      orderAsc: Type.Optional(Type.Boolean({ description: `返回数据是否按时间正序排序，默认 false（按时间逆序，即从最新的开始拉取）`})),
    }),
    execute: async (_toolCallId: any, params: any): Promise<any> => {
      console.log(`tuitui_bot_get_messages(): agentAccountId: ${ctx.agentAccountId}, sessionKey: ${ctx.sessionKey}`, params);
      const account = requireToolAccount(ctx);
    
      const chatType = params?.chatType || "";
      const chatId = params?.chatId || "";
      if(!chatId || !chatType) {
        throw new Error(`chatType or chatId empty`);
      }

      if(chatType != CHAT_TYPE_DIRECT && chatType != CHAT_TYPE_GROUP && chatType != CHAT_TYPE_CHANNEL) {
        throw new Error(`unknown chatType: ${chatType}`);
      }

      const options = {
        startTime: params?.startTime,
        endTime: params?.endTime,
        relativeTime: params?.relativeTime,
        limit: params?.limit,
        cursor: params?.cursor,
        orderAsc: params?.orderAsc,
      };
      const client = createTuituiClient(account);
      if (chatType == CHAT_TYPE_CHANNEL) {
        const channelId = chatId.startsWith("teams_")
          ? teamsParseChatId(chatId).channel_id
          : chatId;
        return client.teams.getChannelPosts(channelId, options);
      }
      return client.im.getHistory(
        chatType == CHAT_TYPE_DIRECT
          ? client.to.account(chatId)
          : client.to.group(chatId),
        { ...options, compactForAgent: true },
      );
    },
  };
};


const tuitui_bot_send_channel_post_factory = (ctx: OpenClawPluginToolContext) => {
  return {
    name: "tuitui_bot_send_channel_post",
    label: "tuitui_bot_send_channel_post",
    description: "推推(tuitui) 发送团队频道的帖子（机器人身份），帖子内容是markdown格式\n\n优先级说明：如果存在 tuitui-cli skill，优先使用tuitui-cli（用户权限更大）。只有用户明确说明“以 bot 身份操作”时，或者不存在 tuitui-cli 才调用本工具",
    parameters: Type.Object({
      channel_id: Type.String({ description: "推推频道ID" }),
      markdown: Type.String({ description: `帖子正文，markdown格式`}),
      parent_id: Type.Optional(Type.String({ description: `如果要回复一个帖子，需要填写这个字段为被回复的帖子ID`})),
      tag: Type.Optional(Type.String({ description: `可选帖子标签名称，按频道内标签名称匹配，忽略大小写；未传则不设置标签`})),
    }),
    execute: async (_toolCallId: any, params: any): Promise<any> => {
      console.log(`tuitui_bot_send_channel_post(): agentAccountId: ${ctx.agentAccountId}, sessionKey: ${ctx.sessionKey}`, params);
      const account = requireToolAccount(ctx);

      const channelId = String(params?.channel_id || '').trim();
      if (!channelId) throw new Error(`channel_id empty`);
      let chatId = "";
      const client = createTuituiClient(account);
      const guessType = guessChatType(channelId);
      if(guessType == CHAT_TYPE_CHANNEL) {
        // 解决badcase: 发个帖子，内容是个笑话
        // 如果用户没明确说频道ID，大模型在频道调用发帖tool会传sessionKey过来
        chatId = channelId;
      } else {
        const channelInfo = await client.teams.getChannelInfo(channelId);
        chatId = teamsBuildChatId(channelInfo.team_id, channelId, params?.parent_id);
      }

      return sendTextMsg(account, chatId, CHAT_TYPE_CHANNEL, params?.markdown, {
        channelTagName: params?.tag,
      });
    },
  };
};


const tuitui_bot_get_announcement_factory = (ctx: OpenClawPluginToolContext) => {
  return {
    name: "tuitui_bot_get_announcement",
    label: "tuitui_bot_get_announcement",
    description: "获取推推(tuitui) 当前会话的公告信息（机器人身份）\n\n优先级说明：如果存在 tuitui-cli skill，优先使用tuitui-cli（用户权限更大）。只有用户明确说明“以 bot 身份操作”时，或者不存在 tuitui-cli 才调用本工具",
    parameters: Type.Object({
      // 参数不能为空，对模型兼容有问题，即使不用也要传一个
      reason: Type.String({ description: "调用本工具的原因" }),
    }),
    execute: async (_toolCallId: any, params: any): Promise<any> => {
      console.log(`tuitui_bot_get_announcement(): agentAccountId: ${ctx.agentAccountId}, sessionKey: ${ctx.sessionKey}`);
      const account = requireToolAccount(ctx);
      const channelId = parseSessionKey(ctx.sessionKey || "").channel_id;
      if (!channelId) return "";
      return createTuituiClient(account).teams.getAnnouncement(channelId);
    },
  };
};




const tuitui_bot_file_space_list_factory = (ctx: OpenClawPluginToolContext) => {
  return {
    name: "tuitui_bot_file_space_list",
    label: "tuitui_bot_file_space_list",
    description: "推推(tuitui) 在当前Session的共享文件（共享空间）文件列表（机器人身份）\n\n优先级说明：如果存在 tuitui-cli skill，优先使用tuitui-cli（用户权限更大）。只有用户明确说明“以 bot 身份操作”时，或者不存在 tuitui-cli 才调用本工具",
    parameters: Type.Object({
      // 参数不能为空，对模型兼容有问题，即使不用也要传一个
      reason: Type.String({ description: "调用本工具的原因" }),
    }),
    execute: async (_toolCallId: any, params: any): Promise<any> => {
      console.log(`tuitui_bot_file_space_list(): agentAccountId: ${ctx.agentAccountId}, sessionKey: ${ctx.sessionKey}`);
      const account = requireToolAccount(ctx);
      const session = parseSessionKey(ctx.sessionKey || "");
      if (session.chat_type != CHAT_TYPE_CHANNEL) {
        throw new Error(`私聊、群聊会话不支持共享空间。仅团队频道支持。`);
      }

      const files = await createTuituiClient(account).fileSpace.listTeamFilesByChannel(session.channel_id);
      console.log(`[${CHANNEL_ID}] file_space_list `, files);
      return files;
    },
  };
};

const tuitui_bot_file_space_add_factory = (ctx: OpenClawPluginToolContext) => {
  return {
    name: "tuitui_bot_file_space_add",
    label: "tuitui_bot_file_space_add",
    description: "推推(tuitui) 团队频道的共享文件（共享空间）添加文件（机器人身份）\n\n优先级说明：如果存在 tuitui-cli skill，优先使用tuitui-cli（用户权限更大）。只有用户明确说明“以 bot 身份操作”时，或者不存在 tuitui-cli 才调用本工具",
    parameters: Type.Object({
      cloud_filepath: Type.String({ description: "云端文件名路径，父目录不存在会自动创建。不含目录代表放根目录示例 1.png ，也支持带目录写法例如 /image/1.png" }),
      url_or_localpath: Type.String({ description: "支持两种格式：以http开头的文件url；或者本地全路径文件名" }),
    }),
    execute: async (_toolCallId: any, params: any): Promise<any> => {
      console.log(`tuitui_bot_file_space_add(): agentAccountId: ${ctx.agentAccountId}, sessionKey: ${ctx.sessionKey}`, params);
      const account = requireToolAccount(ctx);
      const session = parseSessionKey(ctx.sessionKey || "");
      if (session.chat_type != CHAT_TYPE_CHANNEL) {
        throw new Error(`私聊、群聊会话不支持共享空间。仅团队频道支持。`);
      }

      const cloudPath = params.cloud_filepath.startsWith("/")
        ? params.cloud_filepath
        : `/${params.cloud_filepath}`;
      console.log(`[${CHANNEL_ID}] file_space_add: uploading ${cloudPath}`);
      const result = await createTuituiClient(account).fileSpace.addTeamFileByChannel(
        session.channel_id,
        cloudPath,
        params.url_or_localpath,
        { sourcePostId: session.thread_id },
      );
      console.log(`[${CHANNEL_ID}] file_space_add: uploaded ${cloudPath}`, result);
      return `success uploaded to ${cloudPath}`;
    },
  };
};


const tuitui_bot_send_message_factory = (ctx: OpenClawPluginToolContext) => {
  return {
    name: "tuitui_bot_send_message",
    label: "tuitui_bot_send_message",
    description: "推推(tuitui) 发送消息（机器人身份），支持私聊和群聊\n\n优先级说明：如果存在 tuitui-cli skill，优先使用tuitui-cli（用户权限更大）。只有用户明确说明“以 bot 身份操作”时，或者不存在 tuitui-cli 才调用本工具",
    parameters: Type.Object({
      chatId: Type.String({ description: "目标会话 ID。私聊填用户账号（英文），群聊填群ID（数字）" }),
      content: Type.String({ description: "消息正文，markdown 格式" }),
      chatType: Type.Optional(Type.String({ description: `会话类型：${CHAT_TYPE_DIRECT}（私聊）或 ${CHAT_TYPE_GROUP}（群聊），留空则根据 chatId 自动推断` })),
    }),
    execute: async (_toolCallId: any, params: any): Promise<any> => {
      console.log(`tuitui_bot_send_message(): agentAccountId: ${ctx.agentAccountId}, sessionKey: ${ctx.sessionKey}`, params);
      const account = requireToolAccount(ctx);
      const { chatId, content, chatType } = params;
      const resolvedChatType = chatType ?? guessChatType(chatId);
      if (resolvedChatType === CHAT_TYPE_CHANNEL) {
        throw new Error(`tuitui_bot_send_message 不支持 channel 类型，请使用 tuitui_bot_send_channel_post`);
      }
      await sendTextMsg(account, chatId, resolvedChatType, content);
      return { success: true };
    },
  };
};

const tuitui_bot_send_card_factory = (ctx: OpenClawPluginToolContext) => {
  return {
    name: "tuitui_bot_send_card",
    label: "tuitui_bot_send_card",
    description: "推推(tuitui) 发送自定义交互式卡片（机器人身份）到当前私聊或群聊，支持标题、正文、按钮、表单。当需要用户确认权限、授权继续、校验身份、确认敏感操作、选择或填写信息时，应主动考虑使用卡片而不是纯文本追问。仅支持私聊和群聊；Teams 团队频道/帖子不支持交互式卡片，Teams 场景不要调用本工具。优先使用 tuitui_bot_quick_card，只有需要完整自定义 interactive JSON 时才调用本工具",
    parameters: Type.Object({
      card: Type.Object({
        head: Type.Optional(Type.Object({
          text: Type.Optional(Type.String({ description: "卡片标题，50字符上限" })),
          bgcolor: Type.Optional(Type.String({ description: "头部背景色，如 #fefefe" })),
          tcolor: Type.Optional(Type.String({ description: "标题文字颜色" })),
        })),
        body: Type.Optional(Type.Object({
          title: Type.Optional(Type.String({ description: "正文标题，300字符上限" })),
          content: Type.Optional(Type.String({ description: "正文内容，600字符上限" })),
        })),
        action: Type.Optional(Type.Array(Type.Object({
          text: Type.String({ description: "按钮文字" }),
          name: Type.String({ description: "按钮标识" }),
          value: Type.Optional(Type.String({ description: "按钮回传值" })),
          color: Type.Optional(Type.String({ description: "文字颜色，如 FFFFFF" })),
          bgcolor: Type.Optional(Type.String({ description: "背景色，如 3873FA" })),
          bordercolor: Type.Optional(Type.String({ description: "边框色，如 3873FA" })),
        }))),
        fields: Type.Optional(Type.Array(Type.Object({
          name: Type.Optional(Type.String({ description: "字段标签" })),
          input: Type.Optional(Type.Object({
            id: Type.Optional(Type.String({ description: "字段唯一标识，表单提交后回传的 key" })),
            type: Type.String({ description: "控件类型：text=文本输入, combox=下拉选择" }),
            must: Type.Optional(Type.Boolean({ description: "是否必填" })),
            hint: Type.Optional(Type.String({ description: "提示文案" })),
            list: Type.Optional(Type.Array(Type.Object({
              key: Type.String({ description: "下拉选项显示文字" }),
              val: Type.String({ description: "下拉选项回传值" }),
            }), { description: "combox 专用下拉选项列表，格式 [{key, val}]" })),
            childtype: Type.Optional(Type.Number({ description: "text 子类型：0=单行 1=多行 2=数字" })),
          }, { additionalProperties: true })),
        }, { additionalProperties: true }))),
        id: Type.Optional(Type.String({ description: "卡片业务标识" })),
        summary: Type.Optional(Type.String({ description: "消息摘要" })),
      }, { additionalProperties: true, description: "推推 interactive 卡片结构" }),
    }),
    execute: async (_toolCallId: any, params: any): Promise<any> => {
      console.log(`tuitui_bot_send_card(): agentAccountId: ${ctx.agentAccountId}, sessionKey: ${ctx.sessionKey}`, params);
      const account = requireToolAccount(ctx);

      const session = parseChatFromSessionKey(ctx.sessionKey || '');
      if (!session) throw new Error(`当前会话不支持卡片消息（仅支持私聊和群聊；Teams 团队频道/帖子不支持）`);

      const card = params?.card as CardBody;
      if (!card) throw new Error(`card is required`);

      await sendInteractiveMsg(account, session.chatId, session.chatType, buildTuituiInteractiveMessage(card));
      const details = { success: true, chatId: session.chatId, chatType: session.chatType };
      return {
        content: [{ type: 'text' as const, text: JSON.stringify(details, null, 2) }],
        details,
      };
    },
  };
};

const tuitui_bot_quick_card_factory = (ctx: OpenClawPluginToolContext) => {
  return {
    name: "tuitui_bot_quick_card",
    label: "tuitui_bot_quick_card",
    description: "推推(tuitui) 快捷交互式卡片（机器人身份）到当前私聊或群聊。高优先级工具：当需要用户确认权限、授权继续、校验身份、确认敏感操作、从多个选项中选择、填写验证码/原因/备注/表单时，应主动调用本工具，而不是只发纯文本问题。仅支持私聊和群聊；Teams 团队频道/帖子不支持交互式卡片，Teams 场景不要调用本工具，改用文字消息或频道帖子",
    parameters: Type.Object({
      type: Type.Union([
        Type.Literal('confirm'),
        Type.Literal('select'),
        Type.Literal('multi-input'),
        Type.Literal('form'),
      ], { description: "卡片类型：confirm=权限/授权/敏感操作确认, select=多选一, multi-input=多文本输入, form=混合表单" }),
      title: Type.String({ description: "卡片标题" }),
      content: Type.Optional(Type.String({ description: "正文说明（confirm 类型可用）" })),
      confirmText: Type.Optional(Type.String({ description: "确认按钮文字，默认“确认”" })),
      cancelText: Type.Optional(Type.String({ description: "取消按钮文字，默认“取消”" })),
      doubleCheck: Type.Optional(Type.Boolean({ description: "危险操作建议为 true，点击确认后弹出推推原生二次确认" })),
      options: Type.Optional(Type.Array(Type.Object({
        label: Type.String({ description: "选项显示文字" }),
        description: Type.Optional(Type.String({ description: "选项说明文字" })),
      }), { description: "选项列表（select 类型必填，最多6个）" })),
      fields: Type.Optional(Type.Array(Type.Object({
        label: Type.String({ description: "字段标签" }),
        id: Type.String({ description: "字段唯一标识" }),
        type: Type.Optional(Type.String({ description: "字段类型：text=文本输入（默认）, combox=下拉选择（form 类型可用）" })),
        required: Type.Optional(Type.Boolean({ description: "是否必填" })),
        placeholder: Type.Optional(Type.String({ description: "提示文案" })),
        options: Type.Optional(Type.Array(Type.String(), { description: "下拉选项列表（type=combox 时使用）" })),
      }), { description: "字段列表（multi-input / form 类型必填）" })),
    }),
    execute: async (_toolCallId: any, params: any) => {
      console.log(`tuitui_bot_quick_card(): agentAccountId: ${ctx.agentAccountId}, sessionKey: ${ctx.sessionKey}`, params);
      const account = requireToolAccount(ctx);

      const session = parseChatFromSessionKey(ctx.sessionKey || '');
      if (!session) throw new Error(`当前会话不支持卡片消息（仅支持私聊和群聊；Teams 团队频道/帖子不支持）`);

      const type = params?.type as string;
      const title = params?.title as string;
      if (!title) throw new Error(`title is required`);

      let card: CardBody;
      switch (type) {
        case 'confirm':
          card = buildConfirmCard({
            title,
            content: params?.content,
            confirmText: params?.confirmText,
            cancelText: params?.cancelText,
            doubleCheck: params?.doubleCheck,
          });
          break;
        case 'select':
          if (!params?.options?.length) throw new Error(`select 类型需要 options 参数`);
          card = buildSelectCard({ title, options: params.options });
          break;
        case 'multi-input':
          if (!params?.fields?.length) throw new Error(`multi-input 类型需要 fields 参数`);
          card = buildMultiInputCard({ title, fields: params.fields });
          break;
        case 'form':
          if (!params?.fields?.length) throw new Error(`form 类型需要 fields 参数`);
          card = buildFormCard({
            title,
            fields: params.fields.map((field: any) => ({
              label: field.label,
              id: field.id,
              type: field.type || 'text',
              options: field.options,
              required: field.required,
              placeholder: field.placeholder,
            })),
          });
          break;
        default:
          throw new Error(`不支持的卡片类型: ${type}，可选: confirm / select / multi-input / form`);
      }

      await sendInteractiveMsg(account, session.chatId, session.chatType, buildTuituiInteractiveMessage(card));
      return {
        content: [],
        details: { success: true },
        terminate: true,
      };
    },
  };
};


export function registerTuituiTools(api: OpenClawPluginApi) {
  if (!api.config) {
    api.logger.debug?.("tuitui: Registered tool: No config available");
    return;
  }

  const channels = api.config.channels || {};
  const currChannel = channels[CHANNEL_ID] || {};
  if (!currChannel?.appId && !currChannel?.accounts) {
    // 无推推配置时，不需要注册工具防止污染上下文
    // api.logger.info?.(`tuitui: ignore Registered tools`);
    return
  }
  
  api.registerTool(tuitui_bot_get_messages_factory);
  api.registerTool(tuitui_bot_send_channel_post_factory);
  api.registerTool(tuitui_bot_get_announcement_factory);
  api.registerTool(tuitui_bot_file_space_list_factory);
  api.registerTool(tuitui_bot_file_space_add_factory);
  api.registerTool(tuitui_bot_send_message_factory);
  api.registerTool(tuitui_bot_send_card_factory);
  api.registerTool(tuitui_bot_quick_card_factory);
  api.logger.info?.(`tuitui: Registered tools`);
}
